'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["_bodyElement", "_distanceAdjustment", "_insetAdjustment", "animateOnMount", "animateOnToggle", "arrowSize", "autoPlacement", "children", "closeOnOutsideClick", "closeOnTargetLeave", "onChangePlacement", "onCloseComplete", "onFocusLeave", "onOpenChange", "open", "openOnTargetEnter", "pinToConstraint", "pinToOutOfBoundsTarget", "placement", "popoverRef", "shouldCloseOnClick", "target", "use", "zIndex"];
import PropTypes from 'prop-types';
import { Fragment, useContext, useEffect, useRef, useState, memo } from 'react';
import { findDOMNode } from '../utils/findDOMNode';
import useMounted from 'react-utils/hooks/useMounted';
import usePrevious from 'react-utils/hooks/usePrevious';
import { JUPITER_LAYER } from 'HubStyleTokens/sizes';
import { ModalTransitionContext } from 'foundations-components/transitional/components/Modal/ModalTransitionContext';
import { isClickWithinContainer } from '../core/EventHandlers';
import SyntheticEvent from '../core/SyntheticEvent';
import deprecated from '../utils/propTypes/deprecated';
import domElementPropType from '../utils/propTypes/domElement';
import refObject from '../utils/propTypes/refObject';
import { getDefaultNode } from '../utils/RootNode';
import ChildrenWrapper from '../utils/ChildrenWrapper';
import UIPopoverFloaty from './internal/UIPopoverFloaty';
import { PLACEMENTS } from './PlacementConstants';
import UIPopoverArrow from './internal/UIPopoverArrow';
import UIPopoverContent from './internal/UIPopoverContent';
import { autoPlacementPropType, pinToConstraintPropType } from './utils/propTypes';
import { USE_CLASSES } from './utils/Use';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getArrowSize = (arrowSize, use) => {
  if (arrowSize != null) return arrowSize;
  if (use === 'tooltip' || use === 'tooltip-danger') return 'small';
  return 'medium';
};
const defaultPinToConstraint = ['top', 'bottom'];
const UIPopover = /*#__PURE__*/memo(props => {
  const {
      _bodyElement,
      _distanceAdjustment = 0,
      _insetAdjustment = 0,
      animateOnMount = false,
      animateOnToggle,
      arrowSize,
      autoPlacement = true,
      children,
      closeOnOutsideClick = true,
      closeOnTargetLeave = false,
      onChangePlacement,
      onCloseComplete,
      onFocusLeave,
      onOpenChange,
      open = false,
      openOnTargetEnter = false,
      pinToConstraint = defaultPinToConstraint,
      pinToOutOfBoundsTarget = false,
      placement = 'top',
      popoverRef,
      shouldCloseOnClick,
      target,
      use = 'default',
      zIndex = parseInt(JUPITER_LAYER, 10)
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);

  /** A ref to the wrapper around `children` */
  const childrenWrapperRef = useRef(null);
  /** Make sure the DOMNode from `children` is up to date (#9667) */
  const [childElement, setChildElement] = useState();
  useEffect(() => {
    /* eslint-disable-next-line react/no-find-dom-node */
    setChildElement(findDOMNode(childrenWrapperRef.current));
  }, [childrenWrapperRef, children]);
  /** The DOM element the floaty is attached to (`target` if given, `children` otherwise) */
  const targetElement = target || childElement;
  const {
    transitioning: inModalTransition
  } = useContext(ModalTransitionContext);

  // If `closeComplete` is `false`, we're in a close transition
  const [closeComplete, setCloseComplete] = useState(true);

  /** `contentOpen` triggers open/close transitions */
  const contentOpen = open && !inModalTransition && !!targetElement;

  /** `tetherOpen` determines whether the popover is in the DOM */
  const tetherOpen = contentOpen || !closeComplete;

  // If a close transition is starting, update the closeComplete state.
  const prevContentOpen = usePrevious(contentOpen);
  if (prevContentOpen && !contentOpen && closeComplete) {
    setCloseComplete(false);
  }

  /** Ref to the `UIPopoverContent` instance */
  const contentRef = useRef();

  /** The DOM node that the popup content will render in (defaults to `document.body`) */
  const rootNode = _bodyElement || getDefaultNode();
  const prevRootNode = usePrevious(rootNode);

  /** Ref to the `key` used for the popup, which changes when we need a full reset */
  const popupKeyRef = useRef(0);

  // Increment the key if the `rootNode` has changed
  if (prevRootNode && prevRootNode !== rootNode) {
    popupKeyRef.current += 1;
  }

  // Track whether we're mounted
  const mountedRef = useMounted();
  const isMounted = mountedRef.current;

  // Track the IntersectionObserver state for openOnTargetEnter
  const [openOnEnterIntersection, setOpenOnEnterIntersection] = useState();

  // Workaround for react-dnd bug: https://github.com/react-dnd/react-dnd/issues/1146
  const [__internalUpdateCounter, setForceInternalUpdateCounter] = useState(0);
  useEffect(() => {
    if (targetElement != null || childrenWrapperRef.current == null) return undefined;
    const timer = requestAnimationFrame(() => {
      /* eslint-disable-next-line react/no-find-dom-node */
      const newTargetElement = findDOMNode(childrenWrapperRef.current);
      if (targetElement !== newTargetElement) {
        if (window.newrelic) {
          window.newrelic.addPageAction('tetherless-dnd-internal-rerender', {
            newTargetTagName: newTargetElement && newTargetElement.tagName || 'empty'
          });
        }
        setForceInternalUpdateCounter(i => i < 10 ? i + 1 : i);
      }
    });
    return () => {
      cancelAnimationFrame(timer);
    };
  }, [target, childrenWrapperRef, targetElement]);
  const shouldAnimateRef = useRef(open ? animateOnMount : animateOnToggle);
  useEffect(() => {
    if (prevContentOpen != null) {
      shouldAnimateRef.current = animateOnToggle;
    }
  }, [prevContentOpen, animateOnToggle]);

  // On open, bind event listeners that can trigger close
  useEffect(() => {
    if (contentOpen && onOpenChange) {
      const handleWindowClick = evt => {
        // If `shouldCloseOnClick` is provided and returns a non-null value, defer to it
        if (shouldCloseOnClick) {
          const shouldClose = shouldCloseOnClick(evt);
          if (shouldClose != null) {
            if (shouldClose) onOpenChange(SyntheticEvent(false));
            return;
          }
        }

        // Otherwise, use our standard algorithm for detecting "outside clicks"
        const clickTarget = evt.target;

        /* eslint-disable-next-line react/no-find-dom-node */
        const contentEl = findDOMNode(contentRef.current);

        // Ignore the click if it's within our Tether target
        if (targetElement == null || targetElement.contains(clickTarget)) return;

        // Ignore the click if it's within our popover container
        if (contentEl && isClickWithinContainer(evt, contentEl)) return;

        // Otherwise, trigger close
        onOpenChange(SyntheticEvent(false));
      };
      let bindClickHandlerFrame;
      if (closeOnOutsideClick) {
        bindClickHandlerFrame = requestAnimationFrame(() => {
          // Wait a tick to prevent the same click that triggered open from triggering close
          addEventListener('click', handleWindowClick);
        });
      }

      // When closeOnTargetLeave is enabled, we use an IntersectionObserver to close the popover if
      // its target leaves the screen due to scrolling (#3856).
      let intersectionObserver;
      if (closeOnTargetLeave && targetElement) {
        const {
          IntersectionObserver
        } = window;
        intersectionObserver = new IntersectionObserver(entries => {
          const {
            isIntersecting
          } = entries[0];
          if (!isIntersecting) {
            onOpenChange(SyntheticEvent(false));
          }
        });
        intersectionObserver.observe(targetElement);
      }
      return () => {
        cancelAnimationFrame(bindClickHandlerFrame);
        removeEventListener('click', handleWindowClick);
        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }
      };
    }
    return undefined;
  }, /* eslint-disable react-hooks/exhaustive-deps */[closeOnOutsideClick, closeOnTargetLeave, contentOpen, shouldCloseOnClick, targetElement] /* eslint-enable react-hooks/exhaustive-deps */);
  useEffect(() => {
    if (openOnTargetEnter && onOpenChange && targetElement) {
      // When openOnTargetEnter is enabled, we use an IntersectionObserver to open the popover if
      // its target is scrolled into view.
      const {
        IntersectionObserver
      } = window;
      const intersectionObserver = new IntersectionObserver(entries => {
        const {
          isIntersecting
        } = entries[0];
        if (isIntersecting && !openOnEnterIntersection && !contentOpen) {
          onOpenChange(SyntheticEvent(true));
        }
        setOpenOnEnterIntersection(isIntersecting);
      });
      intersectionObserver.observe(targetElement);
      return () => {
        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }
      };
    }
    return undefined;
  }, [contentOpen, onOpenChange, openOnEnterIntersection, openOnTargetEnter, targetElement]);
  const handlePlacementChange = evt => {
    if (onChangePlacement) onChangePlacement({
      newPlacement: evt.target.value
    });
  };
  const handleCloseComplete = () => {
    if (onCloseComplete) onCloseComplete();
    if (isMounted) setCloseComplete(true);
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [children && /*#__PURE__*/_jsx(ChildrenWrapper, {
      ref: childrenWrapperRef,
      children: children
    }), tetherOpen && (() => {
      const computedArrowSize = getArrowSize(arrowSize, use);
      const computedArrowParams = UIPopoverArrow.ARROW_SIZES[computedArrowSize];
      return /*#__PURE__*/_jsx(UIPopoverFloaty, {
        arrowWidth: computedArrowParams.width,
        autoPlacement: autoPlacement,
        distance: computedArrowParams.distance + _distanceAdjustment,
        inset: _insetAdjustment,
        onFocusLeave: onFocusLeave,
        onPlacementChange: handlePlacementChange,
        pinToConstraint: pinToConstraint,
        pinToOutOfBoundsTarget: pinToOutOfBoundsTarget,
        placement: placement,
        ref: popoverRef,
        rootNode: rootNode,
        targetElement: targetElement,
        zIndex: zIndex,
        content: /*#__PURE__*/_jsx(UIPopoverContent, Object.assign({}, rest, {
          animate: shouldAnimateRef.current != null ? shouldAnimateRef.current : computedArrowSize !== 'none',
          arrowSize: computedArrowSize,
          "data-component-name": "UIPopover",
          onCloseComplete: handleCloseComplete,
          open: contentOpen,
          ref: contentRef,
          use: use,
          onOpenChange: onOpenChange
        }))
      }, popupKeyRef.current);
    })()]
  });
});
UIPopover.propTypes = {
  _distanceAdjustment: PropTypes.number,
  _insetAdjustment: PropTypes.number,
  _bodyElement: domElementPropType,
  addTargetClasses: deprecated(PropTypes.bool, {
    message: 'UIPopover: setting `addTargetClasses` is no longer necessary and the prop has been removed.',
    key: 'addTargetClasses'
  }),
  animateOnMount: PropTypes.bool,
  animateOnToggle: PropTypes.bool,
  arrowColor: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({
    header: PropTypes.node,
    body: PropTypes.node,
    footer: PropTypes.node,
    step: PropTypes.node
  })]),
  Content: PropTypes.elementType,
  arrowSize: PropTypes.oneOf(Object.keys(UIPopoverArrow.ARROW_SIZES)),
  autoPlacement: autoPlacementPropType,
  children: PropTypes.element,
  closeOnOutsideClick: PropTypes.bool,
  closeOnTargetLeave: PropTypes.bool,
  onChangePlacement: PropTypes.func,
  onCloseComplete: PropTypes.func,
  onCloseStart: PropTypes.func,
  onFocusLeave: PropTypes.func,
  onOpenChange: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onOpenStart: PropTypes.func,
  open: PropTypes.bool,
  openOnTargetEnter: PropTypes.bool,
  pinToConstraint: pinToConstraintPropType,
  pinToOutOfBoundsTarget: PropTypes.bool,
  popoverRef: refObject,
  placement: PropTypes.oneOf(PLACEMENTS),
  renderContent: PropTypes.func,
  shouldCloseOnClick: PropTypes.func,
  showCloseButton: PropTypes.bool,
  target: domElementPropType,
  use: PropTypes.oneOf(Object.keys(USE_CLASSES)),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  zIndex: PropTypes.number
};
UIPopover.displayName = 'UIPopover';
const UIControlledPopover = UIPopover;
export default UIControlledPopover;